import * as THREE from 'three';
import { buildWeaponModel, WEAPON_BY_NUM, WEAPON_VOXEL, GADGET_BY_NUM, type VoxelModel, type WeaponPartName } from '@vm/content';
import { buildModelGeometry, modelMaterials, type ModelGeometry } from './voxelModel';

export interface WeaponPartGeometry {
  name: WeaponPartName;
  geo: ModelGeometry;
  /** Pivot relative to the grip (meters). */
  pivot: THREE.Vector3;
  /** Unit direction the part leaves the weapon during reloads. */
  dir: THREE.Vector3;
  axis: 'x' | 'y' | 'z';
}

export interface CachedWeapon {
  model: VoxelModel;
  /** The complete model (third person, menus). */
  geo: ModelGeometry;
  /** Body without animated parts (view model). */
  body: ModelGeometry;
  parts: WeaponPartGeometry[];
  /** Points relative to the grip, meters. */
  muzzle: THREE.Vector3;
  fore: THREE.Vector3 | null;
  sight: THREE.Vector3;
  length: number;
  /** Distance from the grip to the rear end (meters, +Z). */
  rear: number;
  /** Height of the model top above the grip (meters). */
  top: number;
}

const cache = new Map<string, CachedWeapon>();

function glowFlags(model: VoxelModel): Uint8Array {
  const glow = new Uint8Array(256);
  for (const e of model.emissive) glow[e] = 1;
  return glow;
}

const rel = (p: readonly number[], grip: readonly number[]): THREE.Vector3 =>
  new THREE.Vector3((p[0] - grip[0]) * WEAPON_VOXEL, (p[1] - grip[1]) * WEAPON_VOXEL, (p[2] - grip[2]) * WEAPON_VOXEL);

/** Geometry for a weapon (optionally with a camo), centered on its grip. Barrel points toward -Z. Cached per weapon + camo. */
export function weaponGeometry(num: number, camo?: string | null): CachedWeapon | null {
  const key = `${num}:${camo ?? ''}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const def = WEAPON_BY_NUM[num];
  if (!def) return null;
  const model = buildWeaponModel(def.model, camo);
  const glow = glowFlags(model);
  const { sx, sy, sz, grip } = model;
  const geo = buildModelGeometry(model.grid, sx, sy, sz, model.palette, glow, WEAPON_VOXEL, grip);
  const names = Object.keys(model.parts) as WeaponPartName[];
  let body = geo;
  const parts: WeaponPartGeometry[] = [];
  if (names.length) {
    const rest = model.grid.slice();
    for (const n of names) {
      const p = model.parts[n]!;
      for (let i = 0; i < p.grid.length; i++) if (p.grid[i]) rest[i] = 0;
      parts.push({
        name: n,
        geo: buildModelGeometry(p.grid, sx, sy, sz, model.palette, glow, WEAPON_VOXEL, p.pivot),
        pivot: rel(p.pivot, grip),
        dir: new THREE.Vector3(p.dir[0], p.dir[1], p.dir[2]),
        axis: p.axis,
      });
    }
    body = buildModelGeometry(rest, sx, sy, sz, model.palette, glow, WEAPON_VOXEL, grip);
  }
  const entry: CachedWeapon = {
    model,
    geo,
    body,
    parts,
    muzzle: rel(model.muzzle, grip),
    fore: model.fore ? rel(model.fore, grip) : null,
    sight: rel(model.sight, grip),
    length: sz * WEAPON_VOXEL,
    rear: (sz - grip[2]) * WEAPON_VOXEL,
    top: (sy - grip[1]) * WEAPON_VOXEL,
  };
  cache.set(key, entry);
  return entry;
}

function addMeshes(g: THREE.Group, geo: ModelGeometry, noFog: boolean): void {
  if (geo.lit) g.add(new THREE.Mesh(geo.lit, noFog ? modelMaterials.litNoFog : modelMaterials.lit));
  if (geo.glow) g.add(new THREE.Mesh(geo.glow, noFog ? modelMaterials.glowNoFog : modelMaterials.glow));
}

/** A mesh group for a complete weapon (lit + glow parts), origin at the grip. */
export function weaponObject(num: number, noFog = false, camo?: string | null): THREE.Group {
  const g = new THREE.Group();
  const w = weaponGeometry(num, camo);
  if (!w) return g;
  addMeshes(g, w.geo, noFog);
  g.userData.muzzle = w.muzzle.clone();
  g.userData.rear = w.rear;
  g.userData.top = w.top;
  return g;
}

export interface RiggedWeapon {
  root: THREE.Group;
  /** Animated parts: a pivot group per part (rest transform = identity offset from `rest`). */
  parts: Partial<Record<WeaponPartName, { group: THREE.Group; rest: THREE.Vector3; dir: THREE.Vector3; axis: 'x' | 'y' | 'z' }>>;
  info: CachedWeapon;
}

/** A weapon split into its static body and animatable parts (view model). */
export function riggedWeapon(num: number, camo?: string | null, noFog = true): RiggedWeapon | null {
  const w = weaponGeometry(num, camo);
  if (!w) return null;
  const root = new THREE.Group();
  addMeshes(root, w.body, noFog);
  const parts: RiggedWeapon['parts'] = {};
  for (const p of w.parts) {
    const group = new THREE.Group();
    group.position.copy(p.pivot);
    addMeshes(group, p.geo, noFog);
    root.add(group);
    parts[p.name] = { group, rest: p.pivot.clone(), dir: p.dir.clone(), axis: p.axis };
  }
  return { root, parts, info: w };
}

/** Small colored cube used for thrown gadgets. */
export function gadgetObject(num: number): THREE.Mesh {
  const g = GADGET_BY_NUM[num];
  return new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.14, 0.14), new THREE.MeshLambertMaterial({ color: g?.color ?? 0x5a7a3a }));
}
